import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStudentView } from '../contexts/StudentViewContext';
const BASE_URL = 'https://scores.aprmode.com';
import { authFetch, authPost, authDelete, apiFetch } from '../utils/api';
import { gradeClass, gradeColor, bgColor, gradeHex, pctGrade } from '../utils/grades';
import { formatDate, timeAgo, parseUTC, formatFlightTime, formatDetailTime } from '../utils/dates';
import { distNm, crossTrackFt, bearing, deriveSkillIcons } from '../utils/geo';
import { DataQualityBadge } from '../components/shared/DataQualityBadge';
import { SectionHeader } from '../components/shared/SectionHeader';
import { ScoreRing } from '../components/charts/ScoreRing';
import { VerticalProfile } from '../components/charts/VerticalProfile';
import { HorizontalTrack } from '../components/charts/HorizontalTrack';
import { FleetDistribution } from '../components/charts/FleetDistribution';

const abbreviateFt = (text) => {
  if (!text) return text;
  // e.g. "8,000ft" -> "8K ft", "11,000ft" -> "11K ft", "8,000" -> "8K" when followed by ft/feet
  return text.replace(/(\d{1,3}(?:,\d{3})+|\d{4,})\s*ft\b/gi, (match, num) => {
    const n = Number(num.replace(/,/g, ''));
    if (n >= 1000) {
      const k = n / 1000;
      return (Number.isInteger(k) ? k : Math.round(k * 10) / 10) + 'Kft';
    }
    return match;
  });
};

const fmtNum = (n) => { if (n == null) return null; const v = Number(n); return v >= 1000 ? (Number.isInteger(v/100) ? Math.round(v/1000) + 'K' : (Math.round(v/100)/10) + 'K') : String(v); };

function PlanPage() {
  const navigate = useNavigate();
  const { user, token, settings } = useAuth();
  // Restore previous briefing from session
  const _savedPlan = (() => { try { const p = JSON.parse(sessionStorage.getItem('bmac3_nav_plan') || 'null'); if (p?.result && !Object.values(p.result.assessments || {}).some(a => a.by_runway)) { sessionStorage.removeItem('bmac3_nav_plan'); return null; } return p; } catch { return null; } })();
  const [departure, setDeparture] = useState(_savedPlan?.departure || '');
  const [arrival, setArrival] = useState(_savedPlan?.arrival || '');
  const [depResults, setDepResults] = useState([]);
  const [arrResults, setArrResults] = useState([]);
  const [depName, setDepName] = useState(_savedPlan?.depName || '');
  const [arrName, setArrName] = useState(_savedPlan?.arrName || '');
  const [altitude, setAltitude] = useState(_savedPlan?.altitude || '');
  const [depOffset, setDepOffset] = useState(_savedPlan?.depOffset ?? 1);
  const [flightDuration, setFlightDuration] = useState(_savedPlan?.flightDuration ?? 1);
  const [altimeterUS, setAltimeterUS] = useState(true);
  const [minimumsOptions, setMinimumsOptions] = useState([]);  // [{id, label, mins}]
  const [selectedMinsId, setSelectedMinsId] = useState('personal');  // 'personal' | 'none' | template id | 'school_frat'
  const [studentFrat, setStudentFrat] = useState(null);  // active flight-school enrollment FRAT (advisory in WILCO)
  const [showMinsSheet, setShowMinsSheet] = useState(false);
  const [planCollapsed, setPlanCollapsed] = useState(_savedPlan?.planCollapsed ?? false);
  const [tail, setTail] = useState(_savedPlan?.tail || '');
  const [myAircraft, setMyAircraft] = useState([]);

  // Plan history (last 5) — loaded from API, persisted per-user
  const [planHistory, setPlanHistory] = useState([]);
  useEffect(() => {
    if (!token) return;
    authFetch('/api/user/plan-history', token)
      .then(d => { if (d.plan_history?.length) setPlanHistory(d.plan_history); })
      .catch(() => {
        // fallback to localStorage if API fails
        try { const h = JSON.parse(localStorage.getItem('bmac3_plan_history') || '[]'); if (h.length) setPlanHistory(h); } catch {}
      });
  }, [token]);

  // Aircraft I fly — for the brief AC selector (drives TOLD lookup server-side)
  useEffect(() => {
    if (!token) return;
    authFetch('/api/user/aircraft', token)
      .then(d => {
        const ac = d.aircraft || [];
        setMyAircraft(ac);
      })
      .catch(() => {});
  }, [token]);

  // Selected runway per airport — initialised from server recommended_runway on each fetch
  const [selectedRunways, setSelectedRunways] = useState({});  // { KHPN: '34', KBED: '29' }
  const [rwyLoading, setRwyLoading] = useState({});  // { KHPN: true } while fetching out-of-envelope rwy

  // Compute ETD/ETA times from offsets
  const getTimeStr = (hoursFromNow) => {
    const d = new Date(Date.now() + hoursFromNow * 3600000);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };
  const etdTime = getTimeStr(depOffset);
  const etaTime = getTimeStr(depOffset + flightDuration);

  // Generate offset options: 0, 0.5, 1, ... 18
  const depOptions = [];
  for (let h = 0; h <= 18; h += 0.5) {
    depOptions.push({ value: h, label: h === 0 ? 'Now' : `+${h}h`, time: getTimeStr(h) });
  }
  const durOptions = [];
  for (let h = 0.5; h <= 8; h += 0.5) {
    durOptions.push({ value: h, label: `${h}h`, time: getTimeStr(depOffset + h) });
  }
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(_savedPlan?.result || null);
  const [showAdvisories, setShowAdvisories] = useState(false);
  const [showContactCfi, setShowContactCfi] = useState(false);
  const [linkedCfis, setLinkedCfis] = useState([]);
  const [showDepWx, setShowDepWx] = useState(false);
  const [showArrWx, setShowArrWx] = useState(false);
  const [focusedAirport, setFocusedAirport] = useState(null); // icao string or null
  const [showNotams, setShowNotams] = useState(false);
  const [notamDetail, setNotamDetail] = useState(null);
  const [wxDetail, setWxDetail] = useState(null);
  const [riskDetail, setRiskDetail] = useState(null);
  const [selectedNotam, setSelectedNotam] = useState(null);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  // Expand plan form when landing on page fresh (no prior result)
  useEffect(() => {
    if (!_savedPlan?.result) setPlanCollapsed(false);
  }, []);

  // Read altimeter setting from cached auth context
  useEffect(() => {
    setAltimeterUS(settings.altimeter_us !== false);
  }, [settings.altimeter_us]);

  // Load minimums options (personal + inherited templates)
  useEffect(() => {
    if (!token) return;
    authFetch('/api/user/minimums', token).then(d => {
      const opts = [];
      if (d.minimums) {
        const m = d.minimums;
        const parts = [
          m.crosswind_kt ? `XW ${m.crosswind_kt}kt` : null,
          m.gust_kt      ? `Gust ${m.gust_kt}kt`   : null,
          m.ceiling      ? `Ceil ${fmtNum(m.ceiling)}ft`  : null,
          m.visibility   ? `Vis ${m.visibility}SM`  : null,
        ].filter(Boolean);
        opts.push({ id: 'personal', label: 'My Minimums', subtitle: parts.join(' · ') || 'No limits set', mins: m });
      }
      for (const t of (d.inherited_templates || [])) {
        // Normalize template minimums to pilot_minimums shape
        const tm = t.minimums || {};
        const normalized = {
          crosswind_kt: tm.crosswind ?? tm.crosswind_kt ?? null,
          gust_kt:      tm.gust      ?? tm.gust_kt      ?? null,
          ceiling:      tm.ceiling   ?? null,
          visibility:   tm.visibility ?? null,
          wind_max:     tm.wind      ?? tm.wind_max     ?? null,
        };
        const parts = [
          normalized.crosswind_kt ? `XW ${normalized.crosswind_kt}kt`  : null,
          normalized.gust_kt      ? `Gust ${normalized.gust_kt}kt`     : null,
          normalized.ceiling      ? `Ceil ${fmtNum(normalized.ceiling)}ft`   : null,
          normalized.visibility   ? `Vis ${normalized.visibility}SM`   : null,
        ].filter(Boolean);
        opts.push({ id: `tmpl_${t.id}`, label: t._source_label || t.name, subtitle: parts.join(' · ') || 'School minimums', mins: normalized, icon: '🏫' });
      }
      if (d.student_frat && d.student_frat.tier_name) {
        const fm = d.student_frat.mins || {};
        const parts = [
          fm.crosswind_kt ? `XW ${fm.crosswind_kt}kt` : null,
          fm.gust_kt      ? `Gust ${fm.gust_kt}kt`    : null,
          fm.ceiling      ? `Ceil ${fmtNum(fm.ceiling)}ft` : null,
          fm.visibility   ? `Vis ${fm.visibility}SM`  : null,
        ].filter(Boolean);
        opts.push({ id: 'school_frat', label: `${d.student_frat.school_name} · ${d.student_frat.tier_name}`, subtitle: `${d.student_frat.stage_name || d.student_frat.course} · ${parts.join(' · ')}`, mins: fm, icon: '🎓' });
        setStudentFrat(d.student_frat);
      }
      opts.push({ id: 'none', label: 'No Minimums', subtitle: 'Check conditions only', mins: null });
      setMinimumsOptions(opts);
    }).catch(() => {
      setMinimumsOptions([{ id: 'personal', label: 'My Minimums', subtitle: '', mins: null }]);
    });
  }, [token]);

  const selectedMins = minimumsOptions.find(o => o.id === selectedMinsId);

  const searchAirport = async (q, setResults) => {
    if (q.length < 2) { setResults([]); return; }
    try {
      const d = await apiFetch(`/api/lookup/airport?q=${encodeURIComponent(q)}`);
      setResults(d.airports || []);
    } catch { setResults([]); }
  };

  const selectAp = (ap, setCode, setName, setResults) => {
    setCode(ap.icao_id || ap.faa_id);
    setName(`${ap.facility_name}, ${ap.city || ''} ${ap.state_code || ''}`);
    setResults([]);
  };

  const savePlanToHistory = (dep, arr, alt) => {
    const entry = { departure: dep, arrival: arr, altitude: alt, timestamp: Date.now() };
    const existing = planHistory.filter(h => !(h.departure === dep && h.arrival === arr && h.altitude === alt));
    const updated = [entry, ...existing].slice(0, 5);
    setPlanHistory(updated);
    // Persist to server (cross-device) and keep localStorage as fast cache
    if (token) authPost('/api/user/plan-history', { plan_history: updated }, token).catch(() => {});
    try { localStorage.setItem('bmac3_plan_history', JSON.stringify(updated)); } catch {}
  };

  const loadFromHistory = (entry) => {
    setDeparture(entry.departure);
    setArrival(entry.arrival);
    setAltitude(entry.altitude || '');
    setDepName(''); setArrName('');
    setResult(null);
    setPlanCollapsed(false);
    
    try { const s = JSON.parse(sessionStorage.getItem('bmac3_nav_plan') || '{}'); s.planCollapsed = false; s.result = null; sessionStorage.setItem('bmac3_nav_plan', JSON.stringify(s)); } catch {}
  };

  const runAssessment = async () => {
    if (!departure || !arrival) return;
    setLoading(true); setResult(null); 
    try {
      const d = await authPost('/api/plan/weather', { departure, arrival, altitude, etd_offset_h: depOffset, eta_offset_h: depOffset + flightDuration, minimums_override: selectedMins?.mins ?? undefined, minimums_label: selectedMins?.label, tail_number: tail || undefined }, token);
      setResult(d);
      // Initialise selectedRunways from server-recommended runways
      const initRwys = {};
      for (const [icao, ax] of Object.entries(d.assessments || {})) {
        if (ax.recommended_runway?.runway_id) initRwys[icao] = ax.recommended_runway.runway_id;
      }
      setSelectedRunways(initRwys);
      setPlanCollapsed(true);
      savePlanToHistory(departure, arrival, altitude);
      try { sessionStorage.setItem('bmac3_nav_plan', JSON.stringify({ departure, arrival, altitude, depName, arrName, depOffset, flightDuration, tail, result: d, planCollapsed: true })); } catch {}
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  // Get effective assessment for an airport using server pre-computed by_runway data
  const getEffectiveAssessment = (icao) => {
    const base = result?.assessments?.[icao];
    if (!base) return null;
    const selRwyId = selectedRunways[icao];
    if (!selRwyId) return base;
    const rwyData = base.by_runway?.[selRwyId];
    if (!rwyData) return base;
    return {
      ...base,
      weather: { ...base.weather, crosswind_kt: rwyData.crosswind_kt },
      risks: rwyData.risks,
      status: rwyData.status,
      recommended_runway: rwyData,
    };
  };

  // Handle runway change — always re-fetch so both airports reflect the current selection.
  // newSelRwys merges existing selectedRunways with the new choice, so a dep change followed
  // by an arr change (or vice versa) captures both in the same re-fetch.
  const onRunwayChange = async (icao, rwyId) => {
    const newSelRwys = { ...selectedRunways, [icao]: rwyId };
    setRwyLoading(prev => ({ ...prev, [icao]: true }));
    try {
      const selectedMins = minimumsOptions.find(o => o.id === selectedMinsId);
      const d = await authPost('/api/plan/weather', {
        departure, arrival, altitude,
        etd_offset_h: depOffset, eta_offset_h: depOffset + flightDuration,
        minimums_override: selectedMins?.mins ?? undefined,
        minimums_label: selectedMins?.label,
        tail_number: tail || undefined,
        selected_runways: newSelRwys,
      }, token);
      setResult(d);
      const initRwys = {};
      for (const [ic, ax] of Object.entries(d.assessments || {})) {
        initRwys[ic] = newSelRwys[ic] || ax.recommended_runway?.runway_id;
      }
      setSelectedRunways(initRwys);
    } catch(e) { /* silently keep current */ }
    setRwyLoading(prev => { const n = {...prev}; delete n[icao]; return n; });
  };

  const inputStyle = { padding: 10, background: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 16, width: '100%', boxSizing: 'border-box' };

  const statusColors = { 'go': '#10b981', 'caution': '#eab308', 'no-go': '#ef4444' };
  const statusIcons = { 'go': '✅', 'caution': '⚠️', 'no-go': '❌' };
  const statusLabels = { 'go': 'Within Acceptable Minimums', 'caution': 'Review Conditions', 'no-go': 'You May Want to Derisk' };
  const sevColors = { 'high': '#ef4444', 'caution': '#eab308', 'info': '#3b82f6' };

  const depSearchTimer = useRef(null);
  const arrSearchTimer = useRef(null);
  const handleDepChange = (e) => {
    const v = e.target.value.toUpperCase();
    setDeparture(v); setDepName('');
    clearTimeout(depSearchTimer.current);
    depSearchTimer.current = setTimeout(() => searchAirport(v, setDepResults), 300);
  };
  const handleArrChange = (e) => {
    const v = e.target.value.toUpperCase();
    setArrival(v); setArrName('');
    clearTimeout(arrSearchTimer.current);
    arrSearchTimer.current = setTimeout(() => searchAirport(v, setArrResults), 300);
  };

  const formatAltimeter = (wx) => {
    if (altimeterUS) {
      const v = wx.altimeter_inhg;
      return v != null ? `${v.toFixed(2)}"` : '—';
    } else {
      const v = wx.altimeter_mb;
      return v != null ? `${Math.round(v)} hPa` : '—';
    }
  };

  const WxCard = ({ icao, label, expanded, onToggle, dimmed }) => {
    const assessment = getEffectiveAssessment(icao);
    if (!assessment) return null;
    const wx = assessment.weather || {};
    const status = assessment.status;
    const risks = assessment.risks || [];
    const airportInfo = result?.airports?.[icao];
    const rwy = assessment.recommended_runway;
    const allRwys = result?.assessments?.[icao]?.all_runways || [];
    const byRwy = result?.assessments?.[icao]?.by_runway || {};
    const viableIds = new Set(result?.assessments?.[icao]?.viable_runway_ids || []);
    const currentRwyId = selectedRunways[icao] || rwy?.runway_id;
    const isRwyLoading = rwyLoading[icao] || false;
    const wxSource = result?.assessments?.[icao]?.wx_source || 'metar';  // 'metar' | 'taf'
    const targetTime = result?.assessments?.[icao]?.target_time || null;  // e.g. '17:30z'
    const isTaf = wxSource === 'taf';

    return (
      <div style={{ padding: 0, overflow: 'hidden', borderBottom: '1px solid var(--border)', opacity: dimmed ? 0.38 : 1, transition: 'opacity 0.25s ease' }}>
        {/* Airport header with status */}
        <div onClick={onToggle} style={{ padding: '12px 14px 12px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer',
          background: 'var(--bg-card)', borderLeft: `3.5px solid ${statusColors[status]}` }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>{icao}</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>{label}{airportInfo ? ` · ${airportInfo.facility_name}` : ''}{airportInfo?.elevation ? ` · ${fmtNum(airportInfo.elevation)}ft MSL` : ''}{rwy ? ` · RWY ${rwy.runway_id}` : ''}</div>
          </div>
          <div style={{ flexShrink: 0, textAlign: 'right' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 15 }}>{statusIcons[status]}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: status === 'go' ? '#064e3b' : status === 'no-go' ? '#991b1b' : '#92400e', lineHeight: 1.25 }}>{statusLabels[status]}</span>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginTop: 4 }}>{expanded ? 'Less ▴' : 'Details ▾'}</div>
          </div>
        </div>

        {/* Weather data */}
        {expanded && <div style={{ padding: '10px 14px', background: 'var(--bg-card)' }}>
          {isTaf && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginBottom: 8, borderRadius: 6,
              background: '#fef9c3', border: '1px solid rgba(180,83,9,0.2)' }}>
              <span style={{ fontSize: 14 }}>📅</span>
              <span style={{ fontSize: 13, color: '#92400e', fontWeight: 700 }}>Forecast conditions{targetTime ? ` for ${targetTime}` : ''} — TAF used (METAR too old)</span>
            </div>
          )}
          {wx.raw && <div onClick={() => setWxDetail({ type: isTaf ? 'TAF (Forecast)' : 'METAR', icao, raw: wx.raw, label })} style={{ fontSize: 14, color: 'var(--text-muted)', background: 'var(--bg-card-content)', padding: '8px 10px', borderRadius: 6, marginBottom: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: isTaf ? '#eab308' : 'var(--accent)', flexShrink: 0 }}>{isTaf ? `TAF ${wx.taf_period || ''}`.trim() : 'METAR'}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{wx.raw}</span>
                <span style={{ fontSize: 14, color: 'var(--text-muted)', flexShrink: 0 }}>{'\u203A'}</span>
              </div>}

          {/* Runway selector */}
          {allRwys.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', marginBottom: 10, borderRadius: 10,
              background: 'var(--bg-card-content)', border: '1px solid var(--border)' }}>
              <div style={{ textAlign: 'center', minWidth: 52, flexShrink: 0 }}>
                {isRwyLoading
                  ? <div style={{ fontSize: 28, color: 'var(--text-muted)' }}>…</div>
                  : <select value={currentRwyId || ''} onChange={e => onRunwayChange(icao, e.target.value)}
                      style={{ background: 'transparent', color: 'var(--accent)', border: 'none',
                        padding: 0, fontSize: 30, fontWeight: 900, letterSpacing: '-1px', colorScheme: 'light dark', cursor: 'pointer',
                        appearance: 'none', WebkitAppearance: 'none', width: '100%', textAlign: 'center' }}>
                      {allRwys.map(r => {
                        const rd = byRwy[r.runway_id];
                        const trafficLabel = rd?.traffic;
                        const isViable = viableIds.has(r.runway_id);
                        let suffix = '';
                        if (trafficLabel === 'ga_active' || trafficLabel === 'jet_active' || trafficLabel === 'active') suffix = ' ✈ active';
                        else if (!isViable) suffix = ' ⚠';
                        return <option key={r.runway_id} value={r.runway_id}>{r.runway_id}{suffix}</option>;
                      })}
                    </select>}
                {(() => {
                  const rd = byRwy[currentRwyId];
                  const tl = rd?.traffic;
                  if (tl) return <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}><span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#059669', marginRight: 3, verticalAlign: 'middle' }}></span>active</div>;
                  return null;
                })()}
              </div>
              {rwy && (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 3 }}>Hdg {rwy.heading}° · {rwy.length_ft ? `${Number(rwy.length_ft).toLocaleString()}ft` : ''}</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: rwy.crosswind_kt > 10 ? '#ef4444' : '#059669' }}>XW {rwy.crosswind_kt}kt</span>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>HW {rwy.headwind_kt}kt</span>
                  </div>
                </div>
              )}
              {currentRwyId === rwy?.runway_id
                ? <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>Best<br/>runway ★</div>
                : <div style={{ fontSize: 11, color: '#b45309', textAlign: 'right', cursor: 'pointer', flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); setSelectedRunways(prev => ({ ...prev, [icao]: rwy?.runway_id })); }}>Reset to<br/>best ★</div>}
            </div>
          )}

          {/* Takeoff & Landing Distance — full-width row */}
          {rwy?.performance && (() => {
            const p = rwy.performance;
            const pr = (rwy.risks || []).find(r => r.type === 'performance');
            const color = pr?.severity === 'high' ? '#ef4444' : (pr?.severity === 'caution' || p.out_of_envelope) ? '#b45309' : '#059669';
            const label = p.phase === 'takeoff' ? 'Takeoff' : 'Landing';
            return (
              <div style={{ padding: '10px 12px', marginBottom: 10, borderRadius: 10,
                background: 'var(--bg-card-content)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label} dist (over 50ft)</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color }}>{Number(p.over50_ft).toLocaleString()} ft</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Ground roll {Number(p.ground_roll_ft).toLocaleString()} ft</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{100 - p.margin_pct}% of {Number(p.runway_avail_ft).toLocaleString()} ft</span>
                </div>
                {pr && <div style={{ fontSize: 12, fontWeight: 700, color, marginTop: 5 }}>{pr.msg}</div>}
              </div>
            );
          })()}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
            <WxStat label="Wind" value={wx.wind_speed != null ? `${wx.wind_dir || 'VRB'}° @ ${wx.wind_speed}kt${wx.wind_gust ? ` G${wx.wind_gust}` : ''}` : '—'} />
            <WxStat label="Crosswind" value={wx.crosswind_kt != null ? `${wx.crosswind_kt}kt` : '—'} highlight={wx.crosswind_kt > 10} />
            <WxStat label="Ceiling" value={wx.ceiling != null ? `${fmtNum(wx.ceiling)}ft` : 'CLR'} highlight={wx.ceiling != null && wx.ceiling < 3000} />
            <WxStat label="Visibility" value={wx.visibility != null ? `${wx.visibility}SM` : '—'} highlight={wx.visibility != null && wx.visibility < 5} />
            <WxStat label="Category" value={wx.flight_category || '—'} highlight={wx.flight_category === 'IFR' || wx.flight_category === 'LIFR'} />
            <WxStat label="Altimeter" value={formatAltimeter(wx)} />
            <WxStat label="Elevation" value={result?.airports?.[icao]?.elevation != null ? `${Math.round(Number(result.airports[icao].elevation)).toLocaleString()}ft` : '\u2014'} />
            <WxStat label="Density Alt" value={assessment.density_alt_ft != null ? `${Number(assessment.density_alt_ft).toLocaleString()}ft` : '\u2014'} highlight={assessment.density_alt_ft != null && assessment.density_alt_ft > (Number(result?.airports?.[icao]?.elevation) || 0) + 2500} />
          </div>

          {/* Risk items */}
          {risks.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {risks.map((r, i) => (
                <div key={i} onClick={() => setRiskDetail({ ...r, icao, label })} style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{r.severity === 'high' ? '🔴' : r.severity === 'info' ? '🟦' : '🟡'}</span>
                  <span style={{ fontSize: 14, color: sevColors[r.severity], lineHeight: 1.4, opacity: 0.88, flex: 1 }}>{r.msg}</span>
                  <span style={{ fontSize: 16, color: "var(--text-muted)", flexShrink: 0 }}>›</span>
                </div>
              ))}
            </div>
          )}
          {risks.length === 0 && <div style={{ fontSize: 14, color: '#10b981', padding: '4px 0' }}>✓ All conditions within your experience and minimums</div>}

        </div>}
        {/* TAF forecast if available */}
        {expanded && result?.weather?.[icao]?.taf_raw && (
          <div style={{ padding: '0 14px 12px', background: 'var(--bg-card)' }}>
            
            <div onClick={() => setWxDetail({ type: 'TAF', icao, raw: result.weather[icao].taf_raw, label, source: result.weather[icao].taf_source })} style={{ fontSize: 14, color: 'var(--text-muted)', background: 'var(--bg-card-content)', padding: '8px 10px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>TAF</span>
                <span style={{ fontFamily: 'monospace', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{result.weather[icao].taf_raw.substring(0, 60)}...</span>
                <span style={{ fontSize: 14, color: 'var(--text-muted)', flexShrink: 0 }}>{'\u203A'}</span>
              </div>
          </div>
        )}
      </div>
    );
  };

  const WxStat = ({ label, value, highlight }) => (
    <div style={{ textAlign: 'center', background: 'var(--bg-card-content)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 6px' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: highlight ? '#d97706' : 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>{label}</div>
    </div>
  );

  // Experience summary card
  const SkillsSummary = () => {
    if (!result?.skills_90d) return null;
    const s = result.skills_90d;
    const mx = result.skills_max || {};
    const items = [
      { name: 'Crosswind', count: s['Crosswind'] || 0, max: mx['Crosswind'] ? `${Math.round(mx['Crosswind'])}kt` : '—' },
      { name: 'Gust', count: s['Gust'] || 0, max: mx['Gust'] ? `+${Math.round(mx['Gust'])}kt` : '—' },
      { name: 'Night', count: (s['Night Flight'] || 0), max: null },
      { name: 'IFR', count: s['IFR'] || 0, max: null },
    ].filter(i => i.count > 0 || i.max !== '—');
    if (items.length === 0) return null;

    return (
      <div className="category-card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>📈 Your 90-Day Experience</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map(i => (
            <div key={i.name} style={{ padding: '6px 10px', background: 'var(--bg-body)', borderRadius: 8, fontSize: 14 }}>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{i.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>×{i.count}</span>
              {i.max && <span style={{ color: '#eab308', marginLeft: 4 }}>max {i.max}</span>}
            </div>
          ))}
        </div>
        {result.minimums && (
          <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
            Minimums: {[
              result.minimums.crosswind_kt ? `XW ${result.minimums.crosswind_kt}kt` : '',
              result.minimums.gust_kt ? `Gust ${result.minimums.gust_kt}kt` : '',
              result.minimums.ceiling || '',
              result.minimums.night ? 'Night ✓' : '',
            ].filter(Boolean).join(' · ') || 'None set'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '0 16px 24px' }}>
      <div style={{ padding: '16px 0 0' }}>
        <h2 style={{ color: 'var(--page-bg-title)', margin: '0 0 4px', fontSize: 22, fontWeight: 800 }}>✈️ Quick Brief</h2>
        <p style={{ color: 'var(--page-bg-sub)', margin: '0 0 16px', fontSize: 15 }}>Assess weather conditions against your experience and minimums</p>
      </div>

      {/* Plan block: recent cards + form — collapses to summary bar when assessment is showing */}
      {result && planCollapsed ? (
        <div onClick={() => { setPlanCollapsed(false); try { const s = JSON.parse(sessionStorage.getItem('bmac3_nav_plan') || '{}'); s.planCollapsed = false; sessionStorage.setItem('bmac3_nav_plan', JSON.stringify(s)); } catch {} }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', margin: '0 0 12px', borderRadius: 10,
            background: 'var(--bg-card)', border: '1px solid var(--border)', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent)', letterSpacing: 1 }}>{departure} → {arrival}</span>
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{altitude ? `${fmtNum(altitude)}ft` : ''}</span>
            <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>ETD {etdTime}</span>
          </div>
          <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Edit ›</span>
        </div>
      ) : (
      <div>
        {/* Recent plans */}
        {planHistory.length > 0 && (
          <div style={{ padding: '0 0 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--page-bg-sub)', textTransform: 'uppercase', letterSpacing: 1 }}>Recent Plans</div>
              {result && <button onClick={() => { setPlanCollapsed(true); try { const s = JSON.parse(sessionStorage.getItem('bmac3_nav_plan') || '{}'); s.planCollapsed = true; sessionStorage.setItem('bmac3_nav_plan', JSON.stringify(s)); } catch {} }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', padding: '2px 6px' }}>Collapse ‹</button>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {planHistory.map((h, i) => (
                <button key={i} onClick={() => loadFromHistory(h)}
                  style={{ padding: '8px 12px', background: (departure === h.departure && arrival === h.arrival) ? 'rgba(59,130,246,0.15)' : 'var(--bg-card)',
                    border: (departure === h.departure && arrival === h.arrival) ? '1.5px solid #3b82f6' : '1px solid var(--border)',
                    borderRadius: 8, cursor: 'pointer', textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent)', letterSpacing: 1 }}>{h.departure} → {h.arrival}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{h.altitude ? `${fmtNum(h.altitude)}ft` : 'No alt'} · {new Date(h.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="category-card" style={{ padding: 14, margin: '0 0 12px' }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {/* Departure */}
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>🛫 From</label>
            <input type="text" placeholder="ICAO" value={departure} onChange={handleDepChange}
              autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck="false"
              style={{ ...inputStyle, fontWeight: 700, fontSize: 20, textAlign: 'center', letterSpacing: 2 }} />
            {depName && <div style={{ fontSize: 13, color: '#10b981', marginTop: 3, textAlign: 'center' }}>✓ {depName}</div>}
            {depResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 160, overflowY: 'auto', marginTop: 2 }}>
                {depResults.map(ap => (
                  <div key={ap.icao_id || ap.faa_id} onMouseDown={e => e.preventDefault()}
                    onClick={() => selectAp(ap, setDeparture, setDepName, setDepResults)}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{ap.icao_id || ap.faa_id}</span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>{ap.facility_name}{ap.city ? `, ${ap.city}` : ''} {ap.state_code || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', paddingTop: 18, fontSize: 22, color: 'var(--text-muted)' }}>→</div>
          {/* Arrival */}
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>🛬 To</label>
            <input type="text" placeholder="ICAO" value={arrival} onChange={handleArrChange}
              autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck="false"
              style={{ ...inputStyle, fontWeight: 700, fontSize: 20, textAlign: 'center', letterSpacing: 2 }} />
            {arrName && <div style={{ fontSize: 13, color: '#10b981', marginTop: 3, textAlign: 'center' }}>✓ {arrName}</div>}
            {arrResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 160, overflowY: 'auto', marginTop: 2 }}>
                {arrResults.map(ap => (
                  <div key={ap.icao_id || ap.faa_id} onMouseDown={e => e.preventDefault()}
                    onClick={() => selectAp(ap, setArrival, setArrName, setArrResults)}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{ap.icao_id || ap.faa_id}</span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>{ap.facility_name}{ap.city ? `, ${ap.city}` : ''} {ap.state_code || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>✈️ Aircraft</label>
          <select value={tail} onChange={e => setTail(e.target.value)} style={{ ...inputStyle, colorScheme: 'dark' }}>
            <option value="">— Default aircraft —</option>
            {myAircraft.map(a => (
              <option key={a.tail_number} value={a.tail_number}>{a.tail_number}{a.nickname ? ` · ${a.nickname}` : ''}{a.ac_type ? ` (${a.ac_type})` : ''}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>🔺 Altitude</label>
            <input type="text" placeholder="e.g. 4500" value={altitude} onChange={e => setAltitude(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>🛫 Depart</label>
            <select value={depOffset} onChange={e => setDepOffset(parseFloat(e.target.value))}
              style={{ ...inputStyle, colorScheme: 'dark' }}>
              {depOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label} ({o.time})</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>⏱️ Duration</label>
            <select value={flightDuration} onChange={e => setFlightDuration(parseFloat(e.target.value))}
              style={{ ...inputStyle, colorScheme: 'dark' }}>
              {durOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label} (arr {o.time})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Minimums selector */}
        {/* Minimums - compact pill */}
        {minimumsOptions.length > 0 && selectedMins && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, flexShrink: 0 }}>⚠️ Mins</span>
            <button onClick={() => setShowMinsSheet(true)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(59,130,246,0.35)',
              background: 'rgba(59,130,246,0.08)', cursor: 'pointer', minWidth: 0,
            }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedMins.icon || (selectedMins.id === 'none' ? '❌' : '👤')} {selectedMins.label}
                {selectedMins.subtitle && <span style={{ fontSize: 13, color: 'rgba(59,130,246,0.7)', fontWeight: 400, marginLeft: 6 }}>{selectedMins.subtitle}</span>}
              </span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 8, flexShrink: 0 }}>Change ›</span>
            </button>
          </div>
        )}

        {studentFrat && selectedMinsId !== 'school_frat' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-6px 0 14px', padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(217,119,6,0.35)', background: 'rgba(217,119,6,0.08)', fontSize: 13, color: '#92400e' }}>
            <span>🎓</span>
            <span style={{ flex: 1 }}>You're an enrolled student at <b>{studentFrat.school_name}</b> — the school's <b>{studentFrat.tier_name}</b> FRAT ({studentFrat.stage_name || studentFrat.course}) also applies to your flights.</span>
            <button onClick={() => setSelectedMinsId('school_frat')} style={{ border: 'none', background: 'rgba(217,119,6,0.15)', color: '#92400e', fontWeight: 700, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', flexShrink: 0 }}>Use it</button>
          </div>
        )}

        {/* Minimums bottom sheet */}
        {showMinsSheet && (
          <div onClick={() => setShowMinsSheet(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: '100%', maxWidth: 520, background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', overflow: 'hidden', boxShadow: '0 -8px 32px rgba(0,0,0,0.4)' }}>
              <div style={{ padding: '14px 20px 0', textAlign: 'center' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 14px' }} />
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>Assess Against</div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>Choose which minimums to evaluate conditions against</div>
              </div>
              <div style={{ padding: '0 16px 32px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {minimumsOptions.map(opt => (
                  <button key={opt.id} onClick={() => { setSelectedMinsId(opt.id); setShowMinsSheet(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: 10, border: '1px solid',
                      borderColor: selectedMinsId === opt.id ? '#3b82f6' : 'var(--border)',
                      background: selectedMinsId === opt.id ? 'rgba(59,130,246,0.1)' : 'var(--bg-body)',
                      cursor: 'pointer', textAlign: 'left', width: '100%',
                    }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: selectedMinsId === opt.id ? '#3b82f6' : 'var(--text-primary)' }}>
                        {opt.icon || (opt.id === 'personal' ? '👤' : opt.id === 'none' ? '❌' : '')} {opt.label}
                      </div>
                      {opt.subtitle && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>{opt.subtitle}</div>}
                    </div>
                    {selectedMinsId === opt.id && <span style={{ fontSize: 20, color: '#3b82f6', flexShrink: 0 }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}


        <button onClick={runAssessment} disabled={loading || !departure || !arrival}
          style={{ width: '100%', padding: 12, background: loading ? 'var(--bg-body)' : 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 17, fontWeight: 800, cursor: loading ? 'default' : 'pointer', opacity: (!departure || !arrival) ? 0.4 : 1 }}>
          {loading ? '⏳ Fetching Weather...' : '☁️ Check Weather & Assess Risk'}
        </button>
        </div>
      </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Overall status banner */}
          {(() => {
            const depA = getEffectiveAssessment(result.departure);
            const arrA = getEffectiveAssessment(result.arrival);
            // brief_status is computed server-side in plan.py and returned in the API
            // response. The frontend never re-derives it. All status logic lives in
            // plan_weather() / plan_historical() alongside _build_synopsis().
            const overall = result.brief_status || 'go';
            return (
              <div style={{ padding: '12px 14px', marginBottom: 8, borderRadius: 12,
                background: overall === 'go' ? '#d1fae5' : overall === 'no-go' ? '#fee2e2' : '#fef3c7',
                border: `1.5px solid ${overall === 'go' ? 'rgba(5,150,105,0.3)' : overall === 'no-go' ? 'rgba(220,38,38,0.3)' : 'rgba(180,83,9,0.3)'}`,
                display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Icon + label */}
                <div style={{ fontSize: 20, flexShrink: 0 }}>{statusIcons[overall]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 900, color: overall === 'go' ? '#064e3b' : overall === 'no-go' ? '#991b1b' : '#92400e', letterSpacing: '-0.3px', lineHeight: 1.2 }}>{statusLabels[overall]}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                    <span>{result.departure} → {result.arrival}{altitude ? ` · ${fmtNum(altitude)}ft` : ''} · ETD {etdTime} → ETA {etaTime}</span>
                    {result.minimums_label && <span style={{ padding: '2px 7px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: overall === 'go' ? 'rgba(5,150,105,0.15)' : overall === 'no-go' ? 'rgba(220,38,38,0.12)' : 'rgba(180,83,9,0.12)', color: overall === 'go' ? '#064e3b' : overall === 'no-go' ? '#991b1b' : '#92400e', border: `1px solid ${overall === 'go' ? 'rgba(5,150,105,0.3)' : overall === 'no-go' ? 'rgba(220,38,38,0.25)' : 'rgba(180,83,9,0.25)'}` }}>{overall === 'go' ? '✓' : '⚠️'} {result.minimums_label}</span>}
                  </div>
                </div>
                {/* CFI button */}
                <button onClick={async () => {
                  try {
                    const r = await fetch(BASE_URL + '/api/user/linked-cfis', { headers: { 'Authorization': `Bearer ${token}` } }); const d = await r.json();
                    setLinkedCfis(d.cfis || []);
                  } catch { setLinkedCfis([]); }
                  setShowContactCfi(true);
                }} style={{ flexShrink: 0, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#3b82f6', fontSize: 14, fontWeight: 700 }}>
                  🦇 CFI
                </button>
              </div>
            );
          })()}


          {/* CFI Contact Dialog */}
          {showContactCfi && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
              onClick={() => setShowContactCfi(false)}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 380, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>🦇 Contact CFI</div>
                  <button onClick={() => setShowContactCfi(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ padding: '12px 20px' }}>
                  {linkedCfis.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 15 }}>
                      No linked CFIs found. Link with a CFI or flight school in Settings.
                    </div>
                  ) : linkedCfis.map((cfi, i) => {
                    const briefUrl = `${window.location.origin}/plan?dep=${result.departure}&arr=${result.arrival}&alt=${altitude || ''}`;
                    const smsBody = encodeURIComponent(`Hey ${cfi.name}, planning ${result.departure} → ${result.arrival}. Can you review? ${briefUrl}`);
                    const emailSubj = encodeURIComponent(`Flight Briefing: ${result.departure} → ${result.arrival}`);
                    const emailBody = encodeURIComponent(`Hi ${cfi.name},\n\nPlanning ${result.departure} to ${result.arrival}. Would appreciate your review:\n${briefUrl}\n\nThanks!`);
                    return (
                      <div key={cfi.id} style={{ padding: '12px 0', borderBottom: i < linkedCfis.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#3b82f6' }}>
                            {(cfi.name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{cfi.name}</div>
                            {cfi.home_airport && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>📍 {cfi.home_airport}</div>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {cfi.phone && (
                            <a href={`sms:${cfi.phone}?&body=${smsBody}`}
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, color: '#10b981', fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
                              💬 SMS
                            </a>
                          )}
                          {cfi.email && (
                            <a href={`mailto:${cfi.email}?subject=${emailSubj}&body=${emailBody}`}
                              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 12px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8, color: '#3b82f6', fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
                              ✉ Email
                            </a>
                          )}
                          {!cfi.phone && !cfi.email && (
                            <div style={{ flex: 1, textAlign: 'center', padding: '10px 12px', color: 'var(--text-muted)', fontSize: 14 }}>
                              No contact info on file
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 8, textAlign: 'center',
            background: 'var(--bg-card)', border: '1px solid rgba(245,158,11,0.4)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>
              ⚠️ This is not a substitute for proper flight planning
            </div>
          </div>

          {/* Weather & Advisories Band */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8, opacity: focusedAirport ? 0.38 : 1, transition: 'opacity 0.25s ease' }}>

          {/* Flight Conditions Synopsis */}
          {result.synopsis && (
            <div onClick={() => { setShowDepWx(false); setShowArrWx(false); setFocusedAirport(null); }}
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden',
                cursor: focusedAirport ? 'pointer' : 'default', transition: 'opacity 0.2s', opacity: focusedAirport ? 0.6 : 1 }}>
            <div style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: 18, lineHeight: 1.7, color: 'var(--text-primary)', fontStyle: 'italic' }}>
                {(() => {
                  // Pick synopsis for current runway combination
                  const depSel = selectedRunways[result.departure] || '';
                  const arrSel = selectedRunways[result.arrival] || '';
                  const rwyKey = `${depSel}|${arrSel}`;
                  const activeSynopsis = result.synopsis_by_runway?.[rwyKey] || result.synopsis;
                  const violations = new Set(result.synopsis_violations || []);
                  if (!violations.size) return abbreviateFt(activeSynopsis);
                  // Split synopsis into sentences and underline violations
                  // (use activeSynopsis below)
                  const sentences = activeSynopsis.match(/[^.!?]+[.!?]+/g) || [activeSynopsis];
                  return sentences.map((sentence, i) => {
                    const trimmed = sentence.trim();
                    const isViolation = [...violations].some(v => trimmed.includes(v.replace(/\.$/, '')));
                    const abbrev = abbreviateFt(sentence);
                    return isViolation
                      ? <span key={i} style={{ textDecoration: 'underline', textDecorationColor: '#ef4444', textUnderlineOffset: 3 }}>{abbrev}</span>
                      : <span key={i}>{abbrev}</span>;
                  });
                })()}
              </div>
              {focusedAirport && <div style={{ fontSize: 13, color: 'var(--accent)', marginTop: 6, opacity: 0.8 }}>Tap to close ↑</div>}
            </div>
            </div>
          )}

          {/* Route Advisories */}
          {(() => {
            const advs = result.route_advisories || [];
            if (advs.length === 0) return null;
            const hazardIcons = { 'CONVECTIVE': '⚡', 'TURB': '🌀', 'TURB-HI': '🌀', 'TURB-LO': '🌀', 'ICE': '❄️', 'IFR': '🌫️', 'LLWS': '💨', 'MT_OBSC': '⛰️', 'FZLVL': '❄️', 'M_FZLVL': '❄️' };
            const hazardLabels = { 'CONVECTIVE': 'Convective', 'TURB': 'Turbulence', 'TURB-HI': 'High-Level Turb', 'TURB-LO': 'Low-Level Turb', 'ICE': 'Icing', 'IFR': 'IFR Conditions', 'LLWS': 'Low-Level Wind Shear', 'MT_OBSC': 'Mountain Obscuration', 'FZLVL': 'Freezing Level', 'M_FZLVL': 'Multiple Freezing Levels' };
            const hazardColors = { 'CONVECTIVE': '#ef4444', 'TURB': '#f59e0b', 'TURB-HI': '#f59e0b', 'TURB-LO': '#d97706', 'ICE': '#3b82f6', 'IFR': '#8b5cf6', 'LLWS': '#ef4444', 'MT_OBSC': '#6b7280', 'FZLVL': '#06b6d4', 'M_FZLVL': '#06b6d4' };

            return (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <div onClick={() => setShowAdvisories(!showAdvisories)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'transparent', borderRadius: showAdvisories ? 0 : 0 }}>
                  <span style={{ fontSize: 18 }}>⚠️</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Route Advisories</span>
                  <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: advs.some(a => a.type === 'SIGMET') ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                    color: advs.some(a => a.type === 'SIGMET') ? '#b91c1c' : '#92400e' }}>
                    {advs.length} active
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: showAdvisories ? 'var(--text-muted)' : 'var(--accent)', background: showAdvisories ? 'transparent' : 'rgba(77,142,240,0.12)', padding: '3px 8px', borderRadius: 10, transition: 'all 0.2s' }}>{showAdvisories ? 'Less ▴' : 'Show ▾'}</span>
                </div>
                {showAdvisories && <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg-card-content)' }}>
                  {advs.map((a, i) => {
                    const hc = hazardColors[a.hazard] || '#6b7280';
                    const isSigmet = a.type === 'SIGMET';
                    return (
                      <div key={i} style={{ padding: '6px 4px', borderBottom: i < advs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: 15 }}>{hazardIcons[a.hazard] || '⚠️'}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: hc, opacity: 0.9 }}>{a.type}</span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {hazardLabels[a.hazard] || a.hazard}
                          </span>
                          {isSigmet && <span style={{ fontSize: 11, fontWeight: 800, padding: '1px 6px', borderRadius: 4,
                            background: '#dc2626', color: '#fff' }}>SIGMET</span>}
                        </div>
                        {a.due_to && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 2 }}>{a.due_to}</div>}
                        {a.altitude_text && (
                          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ opacity: 0.7 }}>↕</span> {a.altitude_text}
                            {a.severity && <span style={{ fontSize: 12, fontWeight: 700, padding: '0 5px', borderRadius: 4,
                              background: a.severity === 'SEV' || a.severity >= 5 ? '#ef444430' : '#f59e0b25',
                              color: a.severity === 'SEV' || a.severity >= 5 ? '#ef4444' : '#f59e0b'
                            }}>{typeof a.severity === 'number' ? (a.severity >= 5 ? 'SEV' : 'MOD') : a.severity}</span>}
                          </div>
                        )}
                        {a.fzl_text && <div style={{ fontSize: 13, color: '#06b6d4', marginTop: 2 }}>❄️ Freezing level: {a.fzl_text}</div>}
                        {!a.fzl_text && a.level_text && <div style={{ fontSize: 13, color: '#06b6d4', marginTop: 2 }}>❄️ Freezing level: {a.level_text}</div>}
                        {a.forecast_hour != null && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Forecast +{a.forecast_hour}h</div>}
                      </div>
                    );
                  })}
                </div>}
              </div>
            );
          })()}


          {/* NOTAM Detail Popup */}
          {selectedNotam && <div onClick={() => setSelectedNotam(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 12, maxWidth: 480, width: '100%', maxHeight: '70vh', overflow: 'auto', border: '1px solid var(--border)' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{selectedNotam.icao || selectedNotam.location} — {selectedNotam.plain}</span>
                <span onClick={() => setSelectedNotam(null)} style={{ fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer', padding: '0 4px' }}>{'\u2715'}</span>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Full NOTAM Text</div>
                <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'var(--bg-secondary)', padding: 12, borderRadius: 8 }}>{(selectedNotam.raw_text || '').replace(/<[^>]*>/g, '').replace(/\\r\\n|\\r/g, '\n')}</div>
                <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
                  {selectedNotam.notam_id && <span><b>ID:</b> {selectedNotam.notam_id}</span>}
                  {selectedNotam.category && <span><b>Category:</b> {selectedNotam.category}</span>}
                  {selectedNotam.classification && <span><b>Class:</b> {selectedNotam.classification}</span>}
                  {selectedNotam.effective_start && <span><b>From:</b> {new Date(selectedNotam.effective_start).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                  {selectedNotam.effective_end && <span><b>To:</b> {selectedNotam.effective_end.includes('2099') ? 'PERM' : new Date(selectedNotam.effective_end).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                </div>
              </div>
            </div>
          </div>}
          {/* NOTAM Detail Modal */}
          {notamDetail && ReactDOM.createPortal(
            <div onClick={() => setNotamDetail(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '85vh', overflow: 'auto', padding: '20px 20px 32px', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 19, fontWeight: 700, padding: '5px 14px', borderRadius: 8, background: notamDetail.tier === 'CRITICAL' ? 'rgba(239,68,68,0.12)' : notamDetail.tier === 'OPERATIONAL' ? 'rgba(245,158,11,0.12)' : 'rgba(59,130,246,0.12)', color: notamDetail.tier === 'CRITICAL' ? '#b91c1c' : notamDetail.tier === 'OPERATIONAL' ? '#92400e' : '#1e40af' }}>{notamDetail.tier}</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{notamDetail.icao || notamDetail.location}</span>
                  <span style={{ fontSize: 19, color: 'var(--text-muted)' }}>{notamDetail.category}</span>
                  <span onClick={() => setNotamDetail(null)} style={{ marginLeft: 'auto', fontSize: 24, cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 8px' }}>{'\u2715'}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 18, lineHeight: 1.5 }}>{notamDetail.plain}</div>
                <div style={{ fontSize: 17, fontFamily: 'monospace', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: 10, padding: 16, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{(notamDetail.raw_text || '').replace(/\\r\\n|\\r/g, '\n').replace(/<[^>]*>/g, '')}</div>
                {notamDetail.effective_start && <div style={{ fontSize: 17, color: 'var(--text-muted)', marginTop: 14 }}>
                  {new Date(notamDetail.effective_start).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {notamDetail.effective_end ? ` \u2013 ${new Date(notamDetail.effective_end).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ' \u2013 PERM'}
                </div>}
                {notamDetail.notam_id && <div style={{ fontSize: 16, color: 'var(--text-muted)', marginTop: 6 }}>{notamDetail.notam_id}</div>}
              </div>
            </div>,
            document.body
          )}

          {/* Wx Detail Modal */}
          {wxDetail && ReactDOM.createPortal(
            <div onClick={() => setWxDetail(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '85vh', overflow: 'auto', padding: '20px 20px 32px', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 19, fontWeight: 700, padding: '5px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.12)', color: '#1e40af' }}>{wxDetail.type}</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{wxDetail.icao}</span>
                  <span style={{ fontSize: 19, color: 'var(--text-muted)' }}>{wxDetail.label}</span>
                  <span onClick={() => setWxDetail(null)} style={{ marginLeft: 'auto', fontSize: 24, cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 8px' }}>{'\u2715'}</span>
                </div>
                {wxDetail.source && <div style={{ fontSize: 16, color: 'var(--text-muted)', marginBottom: 12 }}>Source: {wxDetail.source}</div>}
                <div style={{ fontSize: 18, fontFamily: 'monospace', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: 10, padding: 18, lineHeight: 2.0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{wxDetail.raw}</div>
              </div>
            </div>,
            document.body
          )}

          {riskDetail && ReactDOM.createPortal(
            <div onClick={() => setRiskDetail(null)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
              <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg-card)", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 600, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: "var(--border)", margin: "0 auto 20px" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, padding: "4px 12px", borderRadius: 8,
                    background: riskDetail.severity === "high" ? "#ef444420" : riskDetail.severity === "caution" ? "#eab30820" : "#3b82f620",
                    color: riskDetail.severity === "high" ? "#ef4444" : riskDetail.severity === "caution" ? "#eab308" : "#3b82f6" }}>
                    {riskDetail.severity === "high" ? "No-Go Flag" : riskDetail.severity === "caution" ? "Caution" : "Advisory"}
                  </span>
                  <span style={{ fontSize: 16, color: "var(--text-muted)" }}>{riskDetail.icao}</span>
                  <span onClick={() => setRiskDetail(null)} style={{ marginLeft: "auto", fontSize: 24, cursor: "pointer", color: "var(--text-muted)", padding: "4px 8px" }}>✕</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.45,
                  color: riskDetail.severity === "high" ? "#ef4444" : riskDetail.severity === "caution" ? "#eab308" : "#3b82f6" }}>
                  {riskDetail.msg}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* NOTAMs - Grouped by Relevancy */}
          {(() => {
            const ng = result.notams_grouped;
            if (!ng || !ng.summary || ng.summary.total === 0) return null;
            // Deduplicate within each tier by (icao + plain text) — FAA sometimes issues
            // the same NOTAM twice with different IDs when extending a closure
            const dedup = (arr) => {
              const seen = new Set();
              return (arr || []).filter(n => {
                const key = `${n.icao || n.location}|${(n.plain || n.category || '').toLowerCase().trim()}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
            };
            const s = ng.summary;
            const catIcons = { RWY: '\u{1F6EC}', TWY: '\u{1F500}', LIGHTING: '\u{1F4A1}', NAV: '\u{1F4E1}', NAVAID: '\u{1F4E1}', TFR: '\u{1F6AB}', AIRSPACE: '\u2708\uFE0F', OBST: '\u{1F3D7}\uFE0F', SVC: '\u{1F4FB}', AD: '\u{1F3E2}', PROC: '\u{1F4CB}', IAP: '\u{1F4CB}', SID: '\u{1F4CB}', LTA: '\u{1F4DD}', OTHER: '\u{1F4DD}' };
            const SectionHeader = ({ label, color, icon, count }) => (
              <div onClick={(e) => { const body = e.currentTarget.nextElementSibling; if(body) { body.style.display = body.style.display === 'none' ? 'block' : 'none'; } const arrow = e.currentTarget.querySelector('.sec-arrow'); if(arrow) arrow.style.transform = body && body.style.display !== 'none' ? 'rotate(180deg)' : 'rotate(0deg)'; }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', cursor: 'pointer', marginTop: 4 }}>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color, opacity: 0.7 }}>({count})</span>
                <span className="sec-arrow" style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--text-muted)', transition: 'transform 0.2s', transform: 'rotate(0deg)' }}>{'\u25BE'}</span>
              </div>
            );
            const NotamRow = ({ n, color }) => (
              <div onClick={() => setNotamDetail(n)} style={{ padding: '4px 10px', marginBottom: 2, borderRadius: 6, background: color ? `${color}12` : 'var(--bg-secondary)', border: color ? `1px solid ${color}20` : 'none', fontSize: 13, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{catIcons[n.category] || '\u{1F4DD}'}</span>
                  <span style={{ fontWeight: 600, color: color || 'var(--text-muted)', minWidth: 35 }}>{n.icao || n.location || '?'}</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 13, flex: 1 }}>{n.plain || n.category}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{'\u203A'}</span>
                </div>
              </div>
            );
            const grpIcon = k => k.startsWith('obst') ? '\u{1F3D7}\uFE0F' : k.startsWith('twy') ? '\u{1F500}' : k.startsWith('iap') ? '\u{1F4CB}' : k.startsWith('dep') ? '\u{1F4CB}' : '\u{1F4DD}';
            return (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <div onClick={() => setShowNotams(!showNotams)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <span style={{ fontSize: 18 }}>{'\u{1F4CB}'}</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>NOTAMs</span>
                  {dedup(ng.critical).length > 0 && <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#b91c1c' }}>{dedup(ng.critical).length} critical</span>}
                  {dedup(ng.operational).length > 0 && <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'rgba(245,158,11,0.12)', color: '#92400e' }}>{dedup(ng.operational).length} ops</span>}
                  {dedup(ng.enroute).length > 0 && <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'rgba(59,130,246,0.12)', color: '#1e40af' }}>{dedup(ng.enroute).length} enroute</span>}
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>{dedup(ng.critical).length + dedup(ng.operational).length + dedup(ng.enroute).length + (s.awareness || 0)} total</span>
                  <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: showNotams ? 'var(--text-muted)' : 'var(--accent)', background: showNotams ? 'transparent' : 'rgba(77,142,240,0.12)', padding: '3px 8px', borderRadius: 10, transition: 'all 0.2s' }}>{showNotams ? 'Less ▴' : 'Show ▾'}</span>
                </div>
                {showNotams && <div style={{ padding: '4px 14px 10px', borderTop: '1px solid var(--border)', background: 'var(--bg-card-content)' }}>

                  {dedup(ng.critical).length > 0 && <>
                    <SectionHeader label="Critical" color="#ef4444" icon={'\u26A0\uFE0F'} count={dedup(ng.critical).length} />
                    <div style={{ display: 'none' }}>
                      {dedup(ng.critical).map((n, i) => <NotamRow key={`cr-${i}`} n={n} color="#ef4444" />)}
                    </div>
                  </>}

                  {dedup(ng.operational).length > 0 && <>
                    <SectionHeader label="Operational" color="#f59e0b" icon={'\u{1F527}'} count={dedup(ng.operational).length} />
                    <div style={{ display: 'none' }}>
                      {dedup(ng.operational).map((n, i) => <NotamRow key={`op-${i}`} n={n} color="#f59e0b" />)}
                    </div>
                  </>}

                  {dedup(ng.enroute).length > 0 && <>
                    <SectionHeader label="Enroute" color="#3b82f6" icon={'\u{1F9ED}'} count={dedup(ng.enroute).length} />
                    <div style={{ display: 'none' }}>
                      {dedup(ng.enroute).map((n, i) => <NotamRow key={`en-${i}`} n={n} color="#3b82f6" />)}
                    </div>
                  </>}

                  {s.awareness > 0 && <>
                    <SectionHeader label="Awareness" color="var(--text-muted)" icon={'\u{1F4DD}'} count={s.awareness} />
                    <div style={{ display: 'none' }}>
                      {Object.entries(ng.grouped || {}).map(([icao, groups]) =>
                        Object.entries(groups).map(([key, g]) => (
                          <div key={`grp-${icao}-${key}`} style={{ padding: '4px 10px', marginBottom: 2, borderRadius: 6, background: 'var(--bg-secondary)', fontSize: 13 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 14 }}>{grpIcon(key)}</span>
                              <span style={{ fontWeight: 600, color: 'var(--text-muted)', minWidth: 35 }}>{icao}</span>
                              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{g.summary}</span>
                            </div>
                          </div>
                        ))
                      )}
                      {(ng.awareness || []).filter(n => !n.group_key).map((n, i) => (
                        <NotamRow key={`aw-${i}`} n={n} />
                      ))}
                    </div>
                  </>}

                </div>}
              </div>
            );
          })()}
          </div>

          {/* Airport weather cards - shown above advisories/NOTAMs */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
          <WxCard icao={result.departure} label="Departure" expanded={showDepWx} onToggle={() => { const next = !showDepWx; setShowDepWx(next); setShowArrWx(false); setFocusedAirport(next ? result.departure : null); }} dimmed={focusedAirport && focusedAirport !== result.departure} />
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
          <WxCard icao={result.arrival} label="Arrival" expanded={showArrWx} onToggle={() => { const next = !showArrWx; setShowArrWx(next); setShowDepWx(false); setFocusedAirport(next ? result.arrival : null); }} dimmed={focusedAirport && focusedAirport !== result.arrival} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Train Page ──────────────────────────────────────────────────────────────

// ─── Course Catalogue Browser ────────────────────────────────────────────────

export { PlanPage };
export default PlanPage;
